/**
 * Spec 010 US1 — `RemoteEngineAdapter` body (PR3j).
 *
 * PR3i landed the construction surface; PR3j wires the real
 * `run()` + `cancel()` against the server's HTTP+SSE contract
 * (`specs/010-cloud-gpu-service/contracts/http-api.md` +
 *  `contracts/sse-events.md`). Public interface unchanged.
 *
 * Surface:
 *
 * - `run(request, onProgress)` POSTs `/v1/jobs` with the canonical
 *   IRModule JSON + seed + n_spins; on 202 takes the returned
 *   `job_id` as the `RunId`; opens an SSE stream via `fetch()` (not
 *   `EventSource` — the latter cannot send `Authorization` headers
 *   per WHATWG SSE; see spec 010 research.md R11); parses the
 *   stream's `progress` / `done` / `error` / `cancelled` events;
 *   routes `progress` to the user's `onProgress` callback; resolves
 *   the run's Promise with the `done` event's embedded
 *   `MetricsResult`; rejects with appropriate `EngineError`
 *   subclasses on `error` / `cancelled`.
 *
 * - `cancel(runId)` POSTs `/v1/jobs/<runId>/cancel` and aborts the
 *   in-flight SSE fetch via `AbortController`. Idempotent server-side
 *   (the cancel route returns 204 regardless of job state).
 *
 * - `sampleWinnings()` still rejects — the server only exposes
 *   aggregated MetricsResult, not per-spin winnings (see the
 *   stub-rejection comment from PR3i).
 *
 * - `hardwareLabel` is refreshed from `/v1/info` on every successful
 *   run terminal and persisted via `setRemoteConfig` so the
 *   `EngineBadge` reflects the current label even after a page
 *   reload.
 */

import { setRemoteConfig } from "./remoteConfig";
import {
  CancelledError,
  EngineError,
  type EngineAdapter,
  type MetricsResult,
  type ProgressCallback,
  type ReplayedOutcome,
  type RunId,
  type SimulationRequest,
} from "./types";

/**
 * Options accepted by the `RemoteEngineAdapter` constructor.
 *
 * `url` is the base URL of the server — e.g. `https://gpu.mad9000.com:8443`.
 * Path segments are appended by the adapter (`/v1/jobs`, etc.).
 *
 * `token` is the bearer secret the operator generated via
 * `openssl rand -base64 48` on the box; designers paste it into the
 * CloudGpuModal once and it persists in localStorage thereafter.
 *
 * `hardwareLabel` is optional on construction; the adapter refreshes
 * it from `GET /v1/info` on every successful job result and persists
 * the refreshed value through `setRemoteConfig` so the EngineBadge
 * shows the current label even after a page reload.
 */
export interface RemoteEngineAdapterOptions {
  url: string;
  token: string;
  hardwareLabel?: string;
}

/**
 * Shape of one SSE event after parsing. Matches `sse-events.md` —
 * the wire `data:` payload is the embedded JSON; `kind` is folded
 * in via the `event:` line for legacy EventSource consumers (the
 * adapter trusts the JSON `kind` over the `event:` line when they
 * disagree).
 */
interface ParsedSseEvent {
  id?: number;
  data: Record<string, unknown>;
}

const MAX_BUFFER_BYTES = 16 * 1024 * 1024;

/**
 * Adapter that dispatches `EngineAdapter.run()` calls to a remote
 * `slot-engine-server` (spec 010) over HTTPS + SSE.
 */
export class RemoteEngineAdapter implements EngineAdapter {
  readonly mode = "webgpu" as const;

  readonly url: string;
  readonly token: string;
  hardwareLabel: string | undefined;

  private readonly inflight = new Map<RunId, AbortController>();

  /**
   * Maps the placeholder `pending-*` RunId handed back from `run()`
   * to the server's `job_id` once `POST /v1/jobs` returns. `cancel()`
   * consults this so a caller who holds only the placeholder can
   * still forward the cancel to the server (CR catch). Entry is
   * deleted when the run terminates.
   */
  private readonly placeholderToJobId = new Map<RunId, RunId>();

  constructor(options: RemoteEngineAdapterOptions) {
    if (options.url === "") {
      throw new Error("RemoteEngineAdapter: url must be non-empty");
    }
    if (options.token === "") {
      throw new Error("RemoteEngineAdapter: token must be non-empty");
    }
    try {
      // eslint-disable-next-line no-new -- side-effect-free validation only
      new URL(options.url);
    } catch {
      throw new Error(
        `RemoteEngineAdapter: url must be a valid URL (got: ${options.url})`,
      );
    }
    this.url = options.url.replace(/\/+$/, "");
    this.token = options.token;
    this.hardwareLabel = options.hardwareLabel;
  }

  run(
    request: SimulationRequest,
    onProgress?: ProgressCallback,
  ): [RunId, Promise<MetricsResult>] {
    const controller = new AbortController();
    // Placeholder RunId — used as the caller-facing handle. The
    // adapter tracks the server's `job_id` separately via
    // `placeholderToJobId` once submit completes; `cancel(placeholder)`
    // consults that mapping to forward to the server.
    const placeholder: RunId = `pending-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    this.inflight.set(placeholder, controller);

    const promise = this.executeRun(
      request,
      controller,
      placeholder,
      onProgress,
    ).finally(() => {
      this.inflight.delete(placeholder);
      this.placeholderToJobId.delete(placeholder);
    });
    return [placeholder, promise];
  }

  cancel(runId: RunId): void {
    const controller = this.inflight.get(runId);
    if (controller !== undefined) {
      controller.abort();
    }
    // Resolve the placeholder handed back from `run()` to the real
    // server `job_id`; if `cancel(jobId)` is called directly the
    // mapping look-up is a no-op and we use the passed id verbatim.
    const serverJobId = this.placeholderToJobId.get(runId) ?? runId;
    if (serverJobId.startsWith("pending-")) {
      // Submit hasn't returned a job_id yet — local abort only.
      return;
    }
    void fetch(
      `${this.url}/v1/jobs/${encodeURIComponent(serverJobId)}/cancel`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${this.token}` },
      },
    ).catch(() => {
      // Cancel is best-effort fire-and-forget; the dispatcher observes
      // the in-memory flag at the next chunk boundary regardless of
      // whether the HTTP response succeeded.
    });
  }

  sampleWinnings(_request: SimulationRequest): Promise<number[]> {
    return Promise.reject(
      new EngineError(
        "RemoteEngineAdapter does not support sampleWinnings — the server " +
          "exposes aggregated MetricsResult only; per-spin winnings are a " +
          "local-only convenience (used by the RTP-over-spins chart).",
        "REMOTE_ADAPTER_NO_SAMPLE_WINNINGS",
      ),
    );
  }

  sampleOutcomes(_request: SimulationRequest): Promise<ReplayedOutcome[]> {
    return Promise.reject(
      new EngineError(
        "RemoteEngineAdapter does not support sampleOutcomes — the server " +
          "exposes aggregated MetricsResult only; per-spin outcomes are a " +
          "local-only convenience (used by the spin-replay view).",
        "REMOTE_ADAPTER_NO_SAMPLE_OUTCOMES",
      ),
    );
  }

  private async executeRun(
    request: SimulationRequest,
    controller: AbortController,
    placeholder: RunId,
    onProgress: ProgressCallback | undefined,
  ): Promise<MetricsResult> {
    try {
      return await this.executeRunInner(
        request,
        controller,
        placeholder,
        onProgress,
      );
    } catch (err) {
      // Normalise any abort-related error from `fetch()` / reader to
      // the stable `CancelledError` shape. Without this, a race
      // between local `cancel()` and the server's terminal event
      // surfaces a browser-specific `AbortError` instead of
      // `ENGINE_CANCELLED` (CR catch).
      if (isAbortError(err) || controller.signal.aborted) {
        throw new CancelledError();
      }
      throw err;
    }
  }

  private async executeRunInner(
    request: SimulationRequest,
    controller: AbortController,
    placeholder: RunId,
    onProgress: ProgressCallback | undefined,
  ): Promise<MetricsResult> {
    const submitBody = {
      wire_version: "v1",
      config: request.config,
      seed: request.seed,
      n_spins: request.nSpins,
      engine_kind: "wgpu_native",
    };

    const submitResponse = await fetch(`${this.url}/v1/jobs`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(submitBody),
      signal: controller.signal,
    });

    if (!submitResponse.ok) {
      throw await parseErrorResponse(submitResponse);
    }

    const submitData = (await submitResponse.json()) as {
      job_id?: unknown;
    };
    const jobId = submitData.job_id;
    if (typeof jobId !== "string" || jobId === "") {
      throw new EngineError(
        "server POST /v1/jobs response missing job_id",
        "REMOTE_PROTOCOL_VIOLATION",
      );
    }

    // Re-key the in-flight registry under the server's job_id so a
    // subsequent `cancel(jobId)` from the caller (who has the real
    // id via the returned tuple) routes correctly. ALSO map the
    // placeholder → jobId so a `cancel(placeholder)` can forward the
    // POST /cancel to the server (CR catch — without this, callers
    // who only kept the placeholder lose remote cancel).
    this.inflight.set(jobId, controller);
    this.placeholderToJobId.set(placeholder, jobId);

    const eventsUrl = `${this.url}/v1/jobs/${encodeURIComponent(jobId)}/events`;
    const eventsResponse = await fetch(eventsUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.token}`,
        Accept: "text/event-stream",
      },
      signal: controller.signal,
    });

    if (!eventsResponse.ok) {
      throw await parseErrorResponse(eventsResponse);
    }
    if (eventsResponse.body === null) {
      throw new EngineError(
        "server SSE response has no body stream",
        "REMOTE_PROTOCOL_VIOLATION",
      );
    }

    try {
      const terminal = await readSseStream(
        eventsResponse.body,
        request.nSpins,
        onProgress,
      );
      // Refresh + persist the hardware label from the terminal event
      // if the server included it in `client_meta.hardware`.
      const refreshed = extractHardwareLabel(terminal);
      if (refreshed !== undefined && refreshed !== this.hardwareLabel) {
        this.hardwareLabel = refreshed;
        try {
          setRemoteConfig({
            url: this.url,
            token: this.token,
            hardwareLabel: refreshed,
          });
        } catch {
          // Persistence is best-effort; localStorage failures (quota /
          // disabled / private mode) must not break the run.
        }
      }
      return terminal;
    } finally {
      this.inflight.delete(jobId);
    }
  }
}

/**
 * True if `err` is a `fetch()` / stream-reader abort. Matches the
 * browser-platform DOMException with name `AbortError` and any
 * other abort-shaped surface (Node's older versions throw plain
 * `Error` with the same name).
 */
function isAbortError(err: unknown): boolean {
  if (err === null || typeof err !== "object") {
    return false;
  }
  const name = (err as { name?: unknown }).name;
  return name === "AbortError";
}

/**
 * Parse a server error response body into an `EngineError` (or
 * `CancelledError` when the server reported a cancel). Falls back
 * to a generic message + status code if the body isn't JSON.
 */
async function parseErrorResponse(response: Response): Promise<EngineError> {
  let body: { error?: string; message?: string } = {};
  try {
    body = (await response.json()) as { error?: string; message?: string };
  } catch {
    // Non-JSON body — keep the default empty shape; rely on status.
  }
  const code = body.error ?? `REMOTE_HTTP_${response.status}`;
  const message =
    body.message ?? `server returned ${response.status} ${response.statusText}`;
  return new EngineError(message, code);
}

/**
 * Read the SSE response body to completion (or until a terminal
 * event is observed). Dispatches `progress` events to the user
 * callback; resolves with the final `MetricsResult` on `done`;
 * throws on `error` / `cancelled`.
 *
 * The parser handles partial chunk boundaries and the SSE spec's
 * `\n\n`-terminated event blocks. Keep-alive comment lines
 * (`: keepalive`) are ignored.
 */
async function readSseStream(
  stream: ReadableStream<Uint8Array>,
  totalSpins: number,
  onProgress: ProgressCallback | undefined,
): Promise<MetricsResult> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      if (buffer.length > MAX_BUFFER_BYTES) {
        throw new EngineError(
          "SSE buffer overflow — server is misbehaving (events > 16 MiB)",
          "REMOTE_PROTOCOL_VIOLATION",
        );
      }

      // Process every complete event in the buffer.
      let separatorIdx = buffer.indexOf("\n\n");
      while (separatorIdx !== -1) {
        const raw = buffer.slice(0, separatorIdx);
        buffer = buffer.slice(separatorIdx + 2);
        const parsed = parseSseBlock(raw);
        if (parsed !== undefined) {
          const terminal = dispatchSseEvent(parsed, totalSpins, onProgress);
          if (terminal !== undefined) {
            // Best-effort: cancel the reader so the response body
            // can release its underlying connection.
            void reader.cancel().catch(() => {});
            return terminal;
          }
        }
        separatorIdx = buffer.indexOf("\n\n");
      }
    }
  } finally {
    reader.releaseLock();
  }

  throw new EngineError(
    "SSE stream closed without emitting a terminal event",
    "REMOTE_PROTOCOL_VIOLATION",
  );
}

/**
 * Parse one SSE block (text between `\n\n` boundaries) into a
 * `ParsedSseEvent`. Returns `undefined` for comment-only or
 * malformed blocks so the caller can keep reading.
 */
function parseSseBlock(raw: string): ParsedSseEvent | undefined {
  let id: number | undefined;
  let dataText = "";
  let sawData = false;

  for (const line of raw.split("\n")) {
    if (line === "" || line.startsWith(":")) {
      // Comment or empty — ignore (keep-alive).
      continue;
    }
    const colon = line.indexOf(":");
    if (colon === -1) {
      continue;
    }
    const field = line.slice(0, colon);
    // Per WHATWG SSE: a single space after the colon is stripped.
    const rawValue = line.slice(colon + 1);
    const value = rawValue.startsWith(" ") ? rawValue.slice(1) : rawValue;
    if (field === "id") {
      const parsedId = Number(value);
      if (Number.isFinite(parsedId)) {
        id = parsedId;
      }
    } else if (field === "data") {
      if (sawData) {
        dataText += "\n";
      }
      dataText += value;
      sawData = true;
    }
    // `event:` field is informational; we trust JSON `kind` instead.
  }

  if (!sawData) {
    return undefined;
  }
  try {
    const data = JSON.parse(dataText) as Record<string, unknown>;
    return id !== undefined ? { id, data } : { data };
  } catch {
    // Malformed JSON — skip the event rather than failing the run.
    return undefined;
  }
}

/**
 * Dispatch one parsed SSE event. Returns the MetricsResult on
 * terminal `done`, throws on terminal `error` / `cancelled`,
 * returns `undefined` for non-terminal events (after dispatching
 * any `progress` callback).
 */
function dispatchSseEvent(
  event: ParsedSseEvent,
  totalSpins: number,
  onProgress: ProgressCallback | undefined,
): MetricsResult | undefined {
  const kind = event.data["kind"];
  switch (kind) {
    case "progress": {
      const done = numberOrZero(event.data["done"]);
      if (onProgress !== undefined) {
        try {
          onProgress(done, totalSpins);
        } catch {
          // User callback errors must not break the SSE loop;
          // the run continues + the terminal resolution stands.
        }
      }
      return undefined;
    }
    case "done": {
      const metrics = event.data["metrics"];
      if (metrics === undefined || metrics === null) {
        throw new EngineError(
          "server done event missing `metrics` payload",
          "REMOTE_PROTOCOL_VIOLATION",
        );
      }
      return metrics as MetricsResult;
    }
    case "error": {
      const code =
        typeof event.data["code"] === "string"
          ? (event.data["code"] as string)
          : "REMOTE_ENGINE_ERROR";
      const message =
        typeof event.data["message"] === "string"
          ? (event.data["message"] as string)
          : "server reported an unspecified error";
      throw new EngineError(message, code);
    }
    case "cancelled": {
      throw new CancelledError();
    }
    default:
      // Unknown event kind — ignore. Forward-compat: future server
      // versions may emit additional informational events that the
      // adapter should not crash on.
      return undefined;
  }
}

function numberOrZero(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * Extract the hardware label from a terminal `MetricsResult`'s
 * `client_meta.hardware` block. Returns `undefined` if the server
 * didn't include hardware info (older server versions or non-cloud
 * runs).
 */
function extractHardwareLabel(metrics: MetricsResult): string | undefined {
  const clientMeta = (metrics as unknown as { client_meta?: unknown })
    .client_meta;
  if (
    clientMeta === undefined ||
    clientMeta === null ||
    typeof clientMeta !== "object"
  ) {
    return undefined;
  }
  const hardware = (clientMeta as { hardware?: unknown }).hardware;
  if (
    hardware === undefined ||
    hardware === null ||
    typeof hardware !== "object"
  ) {
    return undefined;
  }
  const gpuName = (hardware as { gpu_name?: unknown }).gpu_name;
  if (typeof gpuName === "string" && gpuName !== "") {
    return gpuName;
  }
  return undefined;
}
